import { Board } from "../../entities/board"
import { BoardResponseV2 } from "./board-response"

type ColumnResponse = BoardResponseV2["columns"][number]
type CardResponse = ColumnResponse["cards"][number]

export type CardViewModel = CardResponse & {
  isTaskCard: boolean
  isTodoCard: boolean
  isOverdue: boolean
  isDueSoon: boolean
  hasDescription: boolean
}

type ColumnViewModel = Omit<ColumnResponse, "cards"> & {
  cards: CardViewModel[]
}

export type BoardViewModelV2 = {
  id: string
  type: BoardResponseV2["type"]
  columns: ColumnViewModel[]
  columnsById: Record<ColumnViewModel["id"], ColumnViewModel>
  columnOrder: string[] // array of column ids in order
  visibleColumnIds: string[] // array of visible column ids in order
  isProjectBoard: boolean
  isTaskBoard: boolean
  hasAllColumnsHidden: boolean
}

const mapCardToViewModel = (card: CardResponse): CardViewModel => {
  const isTaskCard = card.type === "task"
  const isTodoCard = card.type === "todo"
  const isOverdue = card.dueDateStatus === "overdue"
  const isDueSoon = card.dueDateStatus === "dueSoon"
  const hasDescription = !!card.description

  return {
    ...card,
    isTaskCard,
    isTodoCard,
    isOverdue,
    isDueSoon,
    hasDescription,
  }
}

export const mapBoardResponseToViewModel = (boardResponse: BoardResponseV2): BoardViewModelV2 => {
  const columnsById: Record<ColumnViewModel["id"], ColumnViewModel> = {}
  const visibleColumnIds: string[] = []
  const columnOrder: string[] = []
  const columns: ColumnViewModel[] = []

  for (const column of boardResponse.columns) {
    const columnCards = column.cards.map(mapCardToViewModel)
    columnsById[column.id] = { ...column, cards: columnCards }
    columnOrder.splice(column.position, 0, column.id)
    columns.splice(column.position, 0, columnsById[column.id])
    if (column.isVisible) {
      visibleColumnIds.splice(column.position, 0, column.id)
    }
  }

  return {
    id: boardResponse.id,
    type: boardResponse.type,
    columns,
    columnOrder,
    columnsById,
    visibleColumnIds,
    hasAllColumnsHidden: visibleColumnIds.length === 0,
    isProjectBoard: boardResponse.type === "project",
    isTaskBoard: boardResponse.type === "task",
  }
}

export const mapBoardToViewModel = (board: Board): BoardViewModelV2 => {
  const boardData = board.toObject()
  return mapBoardResponseToViewModel({
    id: boardData.id,
    type: boardData.type,
    columns: boardData.columns,
  })
}
